/**
 * Semantic spacing vocabulary shared by every UI2 component that lets a
 * schema author push content around. Authors name an intent ('md bottom
 * spacing'); each platform owns the concrete value behind the token, so the
 * same uiSchema reads correctly on DW, MW, Android and iOS.
 *
 * Token → value mappings live with the platform, NOT here:
 *   - NM:      `ui2Spacing` in `apps/mobile/app/Components/UI2/ui2-styles.ts`
 *   - DW / MW: the `$ui2-spacing-scale` map in
 *              `apps/webapp/src/browser/components/ui2/_ui2-styles.scss`
 */
export type UI2SpacingToken = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export const UI2_SPACING_TOKENS: UI2SpacingToken[] = ['none', 'xs', 'sm', 'md', 'lg', 'xl']

/**
 * Shared spacing mixin — the outer-margin props a UI2 component inherits so a
 * schema author can push it around. NOT a standalone `ui2:` component: there is
 * deliberately no `ui2:spacing`; these props are mixed into real components
 * (e.g. `UI2FormConfigProps`, `UI2FormSectionConfigProps` extend this) and
 * resolved with `resolveSpacingTokens`.
 *
 * Precedence: directional wins over axis, axis wins over shorthand.
 */
export interface UI2SpacingConfigProps {
  /** Spacing on all four edges. Lowest precedence. */
  spacing?: UI2SpacingToken
  /** Spacing on the left and right edges. Overrides `spacing`. */
  spacingHorizontal?: UI2SpacingToken
  /** Spacing on the top and bottom edges. Overrides `spacing`. */
  spacingVertical?: UI2SpacingToken
  /** Spacing on the top edge. Overrides `spacingVertical` and `spacing`. */
  spacingTop?: UI2SpacingToken
  /** Spacing on the bottom edge. Overrides `spacingVertical` and `spacing`. */
  spacingBottom?: UI2SpacingToken
  /** Spacing on the left edge. Overrides `spacingHorizontal` and `spacing`. */
  spacingLeft?: UI2SpacingToken
  /** Spacing on the right edge. Overrides `spacingHorizontal` and `spacing`. */
  spacingRight?: UI2SpacingToken
}

/** One token per edge, after precedence has been applied. */
export interface ResolvedSpacingTokens {
  top?: UI2SpacingToken
  bottom?: UI2SpacingToken
  left?: UI2SpacingToken
  right?: UI2SpacingToken
}

function validate(value: unknown, propName: string): UI2SpacingToken | undefined {
  if (value == null) return undefined
  if (typeof value === 'string' && (UI2_SPACING_TOKENS as string[]).includes(value)) {
    return value as UI2SpacingToken
  }
  console.error(
    `[ui2] \`${propName}\` expects one of ${UI2_SPACING_TOKENS.join(' | ')}; got ${JSON.stringify(value)}. Ignoring.`,
  )
  return undefined
}

export function resolveSpacingTokens(props: UI2SpacingConfigProps): ResolvedSpacingTokens | undefined {
  const all = validate(props.spacing, 'spacing')
  const horizontal = validate(props.spacingHorizontal, 'spacingHorizontal')
  const vertical = validate(props.spacingVertical, 'spacingVertical')

  const resolved: ResolvedSpacingTokens = {
    top: validate(props.spacingTop, 'spacingTop') ?? vertical ?? all,
    bottom: validate(props.spacingBottom, 'spacingBottom') ?? vertical ?? all,
    left: validate(props.spacingLeft, 'spacingLeft') ?? horizontal ?? all,
    right: validate(props.spacingRight, 'spacingRight') ?? horizontal ?? all,
  }

  const hasAny = resolved.top || resolved.bottom || resolved.left || resolved.right
  return hasAny ? resolved : undefined
}
